import dayjs from 'dayjs';
import { Box, IconButton, Typography } from '@mui/material';
import { type System as SystemType } from '@api/system';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { HighlightOff } from '@mui/icons-material';
import { type BillingType, CUT_FEE_MAP } from '@consts/company';
import type { Draft } from '@reduxjs/toolkit';
import { SelectField, colors } from '@components/design-system';
import { PlanChangeDialog } from './PlanChangeDialog';
import { DeletePlanChangeDialog } from './DeletePlangeChangeDialog';

export interface PendingPlanChangeState {
    targetPlanId: BillingType;
    effectiveDate: string;
    justification?: string;
}

export const BillingPlan = ({
    system,
    draft,
    refetch,
    disabled,
    onSchedulePlanChange,
    pendingPlanChange,
    onCancelPending,
}: {
    system: SystemType;
    draft: [Draft<SystemType>, Dispatch<SetStateAction<Draft<SystemType>>>];
    refetch: () => void;
    disabled: boolean;
    onSchedulePlanChange: (state: PendingPlanChangeState) => void;
    pendingPlanChange: PendingPlanChangeState | null;
    onCancelPending: () => void;
}) => {
    const [planChangeDialog, setPlanChangeDialog] = useState<BillingType | null>(null);
    const [deletePlanChangeDialog, setDeletePlanChangeDialog] = useState(false);

    const isDisabled = Boolean(system?.planChangeRequest) || Boolean(pendingPlanChange) || disabled;

    const handleChange = (value: string | number) => {
        setPlanChangeDialog(value as BillingType);
    };

    return (
        <>
            <SelectField
                label='Billing Plan'
                value={draft[0].CutPlanID ?? 0}
                onChange={handleChange}
                disabled={isDisabled}
                options={Object.entries(CUT_FEE_MAP).map(([key, value]) => ({
                    label: value,
                    value: +key,
                }))}
            />

            {(pendingPlanChange || system.planChangeRequest) && (
                <Box
                    display='flex'
                    alignItems='center'
                    gap={1}
                    mt={1}
                    p={1}
                    borderRadius={1}
                    sx={{ backgroundColor: colors.warning.light, border: `1px solid ${colors.warning.main}` }}>
                    <Typography variant='body2' sx={{ flex: 1, fontSize: '0.8125rem' }}>
                        Scheduled change to{' '}
                        <strong>
                            {CUT_FEE_MAP[(system.planChangeRequest?.CutPlanID ?? pendingPlanChange?.targetPlanId)!]}
                        </strong>{' '}
                        on{' '}
                        <strong>
                            {dayjs(
                                (system.planChangeRequest?.EffectiveDate ?? pendingPlanChange?.effectiveDate)!
                            ).format('YYYY-MM-DD')}
                        </strong>
                    </Typography>
                    <IconButton
                        size='small'
                        onClick={system.planChangeRequest ? () => setDeletePlanChangeDialog(true) : onCancelPending}
                        sx={{ color: colors.neutral[600] }}>
                        <HighlightOff fontSize='small' />
                    </IconButton>
                </Box>
            )}

            <PlanChangeDialog
                open={planChangeDialog !== null}
                onClose={() => setPlanChangeDialog(null)}
                onConfirm={({ effectiveDate, justification }) =>
                    onSchedulePlanChange({
                        targetPlanId: planChangeDialog!,
                        effectiveDate,
                        justification,
                    })
                }
                currentPlanId={system.CutPlanID!}
                targetPlanId={planChangeDialog!}
                stationId={system.StationID!}
            />

            {system.planChangeRequest && (
                <DeletePlanChangeDialog
                    open={deletePlanChangeDialog}
                    onClose={() => setDeletePlanChangeDialog(false)}
                    onRefresh={refetch}
                    planId={system.planChangeRequest.PlanChangeRequestID}
                />
            )}
        </>
    );
};
